import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCardOrder } from '../hooks/useCardOrder';
import { withCardIcon } from '../lib/uiIcons';

export interface StackCard {
  id: string;
  /** Card's own title — used as the clickable summary when foldable. */
  title: React.ReactNode;
  /** Optional dim sub-line under the title in the folded head. */
  sub?: React.ReactNode;
  /**
   * The card HAS CONTENT TO SHOW — open it by default as long as the user has
   * not folded it explicitly (LOT E1, 2026-11-02). Computed by the page from
   * data ALREADY in scope (never an extra fetch). Resolution order is
   * `saved prefs > hasData > defaultOpen`. Leave it `false` when in doubt: a
   * folded card beats an empty unfolded one.
   */
  hasData?: boolean;
  node: React.ReactNode;
}

/**
 * CardStack — foldable, DRAGGABLE cards (option cards déplaçables, user
 * 2026-10-24; foldable 2026-10-25, same v0.1.77 details.card pattern).
 *
 * foldable=true: every card renders as <details className="card"> with an
 * rc-card-head summary (chevron ▸ + title). Open/closed persists per user in
 * the SAME ui-prefs payload as the order (openIds). Cards absent from the
 * saved open list fall back to `card.hasData === true` (a card that HAS
 * something to show opens itself, LOT E1) and then to defaultOpen.
 */
export default function CardStack({ pageId, cards, foldable = false, defaultOpen = [] }: {
  pageId: string;
  cards: StackCard[];
  foldable?: boolean;
  defaultOpen?: string[];
}) {
  const { t } = useTranslation();
  const enabled = cards.length > 1;
  const { order, openIds, setOpenIds, organizing, setOrganizing, onDrop, reset, saving } =
    useCardOrder(pageId, cards.map((c) => c.id), enabled || foldable, foldable);
  const dragId = useRef<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  const byId = new Map(cards.map((c) => [c.id, c]));
  const ordered = order.map((id) => byId.get(id)).filter((c): c is StackCard => !!c);
  // cards not in `order` yet (added after a save) go last
  for (const c of cards) if (!ordered.some((o) => o.id === c.id)) ordered.push(c);

  // Open-state resolution order (LOT E1, 2026-11-02): SAVED USER PREFS first
  // (an explicit fold/unfold always wins), then `card.hasData` (the card has
  // something to show), then the page's defaultOpen list.
  const defaultOpenFor = (c: StackCard) => defaultOpen.includes(c.id) || c.hasData === true;
  const isOpen = (id: string) => {
    if (openIds) return openIds.includes(id);
    const c = byId.get(id);
    return c ? defaultOpenFor(c) : defaultOpen.includes(id);
  };
  const toggleOpen = (id: string, next: boolean) => {
    // Seed the explicit pref list from what is EFFECTIVELY open right now
    // (hasData cards included): otherwise the first fold would persist a list
    // that closes every card the user never touched.
    const cur = openIds ?? ordered.filter((c) => isOpen(c.id)).map((c) => c.id);
    const set = new Set(cur);
    if (next) set.add(id); else set.delete(id);
    setOpenIds([...set]);
  };

  const dragProps = (id: string) => organizing
    ? {
        draggable: true,
        onDragStart: (e: React.DragEvent) => { dragId.current = id; e.dataTransfer.effectAllowed = 'move'; },
        onDragOver: (e: React.DragEvent) => { if (dragId.current && dragId.current !== id) { e.preventDefault(); setDragOver(id); } },
        onDragLeave: () => setDragOver((cur) => (cur === id ? null : cur)),
        onDrop: (e: React.DragEvent) => {
          if (dragId.current) { e.preventDefault(); onDrop(dragId.current, id); }
          setDragOver(null); dragId.current = null;
        },
        onDragEnd: () => { setDragOver(null); dragId.current = null; },
        style: { cursor: 'grab', outline: dragOver === id ? '2px dashed var(--accent)' : undefined, outlineOffset: 2 } as React.CSSProperties,
      }
    : {};

  return (
    <div className="card-stack" data-card-stack={pageId}>
      {(enabled || foldable) && (
        <div className="card-organize-bar" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginBottom: 4 }}>
          {saving && <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>{t('cardStack.saving')}</span>}
          {organizing && (
            <button type="button" className="btn btn-small btn-secondary" onClick={reset}>
              {t('cardStack.reset')}
            </button>
          )}
          <button
            type="button"
            className={`btn btn-small ${organizing ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setOrganizing(!organizing)}
            aria-pressed={organizing}
          >
            {organizing ? t('cardStack.done') : t('cardStack.organize')}
          </button>
        </div>
      )}
      {ordered.map(({ id, title, sub, node }) => {
        if (foldable) {
          // LOT U2f — une carte de SECTION porte une icône, prise dans la table
          // unique `src/lib/uiIcons.ts` (clé `<pageId>:<cardId>`), jamais dans
          // les valeurs i18n. Un `title` non textuel (ReactNode) est laissé tel
          // quel : on ne devine rien.
          const headTitle = typeof title === 'string' ? withCardIcon(pageId + ':' + id, title) : title;
          return (
            <details
              key={id}
              className="card"
              data-card-id={id}
              style={{ maxWidth: 1000 }}
              open={isOpen(id)}
              onToggle={(e) => {
                // Uncontrolled usage: React only sets the INITIAL open value.
                // Every toggle (user click OR React re-applying open=) fires
                // this; sync prefs only when the DOM value actually differs
                // from the value we computed (a re-render round-trip is a
                // no-op, a click is a change).
                const now = (e.target as HTMLDetailsElement).open;
                if (now === isOpen(id)) return;
                toggleOpen(id, now);
              }}
              {...dragProps(id)}
            >
              <summary className="rc-card-head">
                <div>
                  <h3 className="rc-header-title">{headTitle}</h3>
                  {sub && <p className="rc-header-sub">{sub}</p>}
                </div>
              </summary>
              <div style={{ marginTop: 12 }}>{node}</div>
            </details>
          );
        }
        return (
          <div key={id} data-card-id={id} {...dragProps(id)}>
            {node}
          </div>
        );
      })}
    </div>
  );
}
